import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Ban, Clock, Copy, Eye, Link2, RefreshCw } from 'lucide-react';
import { parseApiError } from '@ooh/contracts';
import type { Offer, PublicLinkStatus } from '@ooh/contracts';
import { Badge } from '@ooh/ui/components/badge';
import { Button } from '@ooh/ui/components/button';
import { useCreatePublicLink, usePublicLinkStatus, useRevokePublicLink } from '../api/mutations';
import { usePublishVersion } from '@ooh/screens';
import { formatDisplayDate } from '../../../lib/date-format';
import {
  clearCachedPublicLink,
  readCachedPublicLink,
  writeCachedPublicLink,
} from '../lib/public-link-storage';
import { PublicLinkConfirmDialog } from './public-link-confirm-dialog';

/**
 * Which destructive action's double-confirm dialog is open, if any.
 * Regenerating and revoking each invalidate the client's current active link,
 * so both go through PublicLinkConfirmDialog; first-ever generation does not.
 */
type ActiveConfirmDialog = 'regenerate' | 'revoke' | null;

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function resolveFirstViewedAt(status: PublicLinkStatus | undefined): Date | null {
  return status?.firstViewedAt ?? null;
}

/**
 * A generated link's plaintext path, atomically tagged with the linkId it was actually
 * generated for — never derived separately from whatever `status.linkId` happens to be at
 * write/restore time (that was the CU-86cbc4u7r cache-poisoning bug: a stale path from an
 * earlier generate could get persisted under a newer, unrelated linkId after a
 * regenerate/revoke from another tab).
 */
interface GeneratedLink {
  linkId: number;
  relativePath: string;
  expiresAt: Date;
}

/**
 * True only when `generatedLink` was generated for the link that is CURRENTLY active
 * server-side, and that link has not passed its `expiresAt` — an expired-but-not-revoked
 * link still reports `active: true` with the same `linkId` (`getLinkStatus` only checks
 * `revokedAt`), so linkId-matching alone is not enough to treat a cached path as copyable.
 * Prefers `status.expiresAt` (the freshest signal) over the locally-known value.
 */
function isGeneratedLinkValid(
  generatedLink: GeneratedLink | null,
  status: PublicLinkStatus | undefined,
): generatedLink is GeneratedLink {
  if (generatedLink == null || status?.linkId == null) return false;
  if (generatedLink.linkId !== status.linkId) return false;
  const expiresAt = status.expiresAt ?? generatedLink.expiresAt;
  return expiresAt.getTime() > Date.now();
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function resolveActiveGeneratedLink(
  generatedLink: GeneratedLink | null,
  status: PublicLinkStatus | undefined,
): GeneratedLink | null {
  return isGeneratedLinkValid(generatedLink, status) ? generatedLink : null;
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function resolveRelativePath(activeLink: GeneratedLink | null): string | null {
  return activeLink?.relativePath ?? null;
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function resolveDisplayExpiresAt(
  status: PublicLinkStatus | undefined,
  activeLink: GeneratedLink | null,
): Date | null {
  return status?.expiresAt ?? activeLink?.expiresAt ?? null;
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function resolveAbsoluteUrl(canShowCopy: boolean, relativePath: string | null): string | null {
  return canShowCopy ? window.location.origin + relativePath : null;
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function isAnyMutationPending(...pendingFlags: boolean[]): boolean {
  return pendingFlags.some(Boolean);
}

/** Extracted to keep PublicLinkControls under the complexity lint threshold. */
function hasNoLinkYet(hasActiveLink: boolean, canShowCopy: boolean): boolean {
  return !hasActiveLink && !canShowCopy;
}

interface PublicLinkControlsProps {
  offerId: string;
  /** Full offer object — needed for the optimistic-lock `updatedAt` sent when publishing. */
  offer: Offer;
  /** canEdit && hasPublicOfferLinksFlag — resolved in parent */
  canManage: boolean;
  /**
   * Whether the offer has ever been published — resolved in the parent
   * (ClientShareDialog) via useOfferHasPublishedVersion. When false (and known — see
   * isUnknownPublishStatus), clicking "Wygeneruj link" transparently publishes the first
   * version (no change-description prompt) before generating the link, instead of blocking
   * the user (CU-86cbc4u7r).
   */
  hasPublishedVersion: boolean;
  /** True while the hasPublishedVersion check is still loading — disables optimistically. */
  isCheckingPublishStatus: boolean;
  /**
   * True when the hasPublishedVersion check has never produced known-good data (e.g. the
   * versions-list fetch failed) — see useOfferHasPublishedVersion. `hasPublishedVersion` is
   * `false` in this state too, but that does NOT mean "not published" — it means "unknown".
   * Generate must fall through to handleCreate() directly (server is the source of truth)
   * instead of auto-publishing, or an offer that already has a published version would get
   * an unwanted extra version purely because of a transient fetch glitch.
   */
  isUnknownPublishStatus: boolean;
  /** Overridable for tests — defaults to navigator.clipboard.writeText */
  writeToClipboard?: (text: string) => Promise<void>;
  /** Overridable for tests — defaults to window.localStorage */
  storage?: Storage;
}

interface ActiveLinkViewProps {
  absoluteUrl: string | null;
  copyHint: boolean;
  expiresAt: Date | null;
  firstViewedAt: Date | null;
  isRegenerating: boolean;
  isRevoking: boolean;
  labels: {
    active: string;
    copyHint: string;
    expires: (date: string) => string;
    notViewedYet: string;
    regenerate: string;
    regenerateToCopy: string;
    revoke: string;
    viewedAt: (date: string) => string;
  };
  onCopy: () => void;
  onRegenerate: () => void;
  onRevoke: () => void;
}

function ActiveLinkView({
  absoluteUrl,
  copyHint,
  expiresAt,
  firstViewedAt,
  isRegenerating,
  isRevoking,
  labels,
  onCopy,
  onRegenerate,
  onRevoke,
}: ActiveLinkViewProps) {
  return (
    <div className="space-y-3">
      {/* URL pill — only when we hold the path from this session */}
      {absoluteUrl != null ? (
        <Button
          className="group flex h-auto w-full items-center justify-start gap-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-muted/50"
          data-testid="public-link-copy-btn"
          onClick={onCopy}
          title={labels.copyHint}
          type="button"
          variant="ghost"
        >
          <Link2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate font-mono text-xs text-foreground">{absoluteUrl}</span>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
            <Copy className="h-3.5 w-3.5" />
          </span>
        </Button>
      ) : null}

      {/* Active link exists but the URL is not available this session */}
      {copyHint ? (
        <p
          className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
          data-testid="public-link-regenerate-to-copy-hint"
        >
          {labels.regenerateToCopy}
        </p>
      ) : null}

      {/* Status row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
        <Badge className="gap-1 px-2 py-0.5" variant="success">
          <span className="h-1.5 w-1.5 rounded-full bg-status-free" />
          {labels.active}
        </Badge>
        {expiresAt ? (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" strokeWidth={1.8} />
            {labels.expires(formatDisplayDate(expiresAt))}
          </span>
        ) : null}
        {firstViewedAt ? (
          <span
            className="flex items-center gap-1 text-status-free"
            data-testid="public-link-viewed-indicator"
          >
            <Eye className="h-3 w-3" strokeWidth={1.8} />
            {labels.viewedAt(formatDisplayDate(firstViewedAt))}
          </span>
        ) : (
          <span
            className="flex items-center gap-1 text-muted-foreground"
            data-testid="public-link-not-viewed-indicator"
          >
            <Eye className="h-3 w-3" strokeWidth={1.8} />
            {labels.notViewedYet}
          </span>
        )}
      </div>

      {/* Manage actions — low visual weight */}
      <div className="flex items-center gap-3">
        <Button
          className="gap-1.5"
          data-testid="public-link-regenerate-btn"
          disabled={isRegenerating}
          onClick={onRegenerate}
          size="sm"
          type="button"
          variant="outline"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {labels.regenerate}
        </Button>
        <Button
          className="gap-1.5 text-muted-foreground hover:text-destructive"
          data-testid="public-link-revoke-btn"
          disabled={isRevoking}
          onClick={onRevoke}
          size="sm"
          type="button"
          variant="ghost"
        >
          <Ban className="h-3.5 w-3.5" />
          {labels.revoke}
        </Button>
      </div>
    </div>
  );
}

interface InactiveLinkViewProps {
  isCheckingPublishStatus: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  labels: {
    generate: string;
    inactive: string;
  };
}

/**
 * No auto-publish gate here — clicking generate with no published version transparently
 * publishes the first version before generating the link (CU-86cbc4u7r), so the button is
 * only disabled while the publish-status check itself is loading or a mutation is in flight.
 */
function InactiveLinkView({
  isCheckingPublishStatus,
  isGenerating,
  onGenerate,
  labels,
}: InactiveLinkViewProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-border bg-muted/20 px-4 py-3">
        <span className="text-sm text-muted-foreground">{labels.inactive}</span>
        <Button
          data-testid="public-link-generate-btn"
          disabled={isCheckingPublishStatus || isGenerating}
          onClick={onGenerate}
          size="sm"
          type="button"
          className="gap-1.5"
        >
          <Link2 className="h-3.5 w-3.5" />
          {labels.generate}
        </Button>
      </div>
    </div>
  );
}

export function PublicLinkControls({
  offerId,
  offer,
  canManage,
  hasPublishedVersion,
  isCheckingPublishStatus,
  isUnknownPublishStatus,
  writeToClipboard = (text) => navigator.clipboard.writeText(text),
  storage = window.localStorage,
}: PublicLinkControlsProps) {
  const { t } = useTranslation('offers');
  const [generatedLink, setGeneratedLink] = React.useState<GeneratedLink | null>(null);
  const [activeConfirmDialog, setActiveConfirmDialog] = React.useState<ActiveConfirmDialog>(null);

  const { data: status } = usePublicLinkStatus(offerId);
  const createMutation = useCreatePublicLink(offerId);
  const revokeMutation = useRevokePublicLink(offerId);
  const publishVersion = usePublishVersion(offerId);

  const statusLinkId = status?.linkId;

  // Validates the current generatedLink against the latest status (clearing it — and its
  // cache entry — the moment it's no longer valid, e.g. regenerated/revoked/expired), or
  // seeds it from a previously cached entry, validated the same way (CU-86cbc4u7r). A fresh
  // generate/regenerate already sets generatedLink directly with its confirmed linkId (see
  // handleCreate) — this effect does not need to write on that path, only validate/restore.
  React.useEffect(() => {
    if (statusLinkId == null) {
      return;
    }
    if (generatedLink != null) {
      if (!isGeneratedLinkValid(generatedLink, status)) {
        setGeneratedLink(null);
        clearCachedPublicLink(offerId, storage);
      }
      return;
    }
    const cached = readCachedPublicLink(offerId, storage);
    if (isGeneratedLinkValid(cached, status)) {
      setGeneratedLink(cached);
    } else if (cached) {
      clearCachedPublicLink(offerId, storage);
    }
  }, [offerId, statusLinkId, generatedLink, status, storage]);

  if (!canManage) {
    return null;
  }

  const hasActiveLink = status?.active === true;
  const activeLink = resolveActiveGeneratedLink(generatedLink, status);
  const canShowCopy = activeLink != null;
  const canShowRegenerateToCopy = hasActiveLink && !canShowCopy;
  const relativePath = resolveRelativePath(activeLink);
  const expiresAt = resolveDisplayExpiresAt(status, activeLink);

  function handleCreate() {
    createMutation.mutate(undefined, {
      onSuccess: (data) => {
        // The create response returns linkId directly, so the plaintext path is paired
        // with its confirmed linkId synchronously — no need to wait on a status-query
        // refetch to find out which link it belongs to (CU-86cbc4u7r).
        const finalized: GeneratedLink = {
          linkId: data.linkId,
          relativePath: data.path,
          expiresAt: data.expiresAt,
        };
        setGeneratedLink(finalized);
        writeCachedPublicLink(offerId, finalized, storage);
        toast.success(t('publicLink.generated'));
      },
    });
  }

  /**
   * Handles "Wygeneruj link" when no published version exists yet: transparently publishes
   * the first version (no change-description prompt), then generates the link on success —
   * instead of blocking the user behind a separate Versions-tab publish step (CU-86cbc4u7r).
   */
  function handleAutoPublishThenCreate() {
    publishVersion.mutate(
      { updatedAt: offer.updatedAt },
      {
        onSuccess: () => handleCreate(),
        onError: (error) => {
          // 409 (stale offer)/422 (validation) are swallowed by usePublishVersion's own
          // onError — that's meant for a dialog's inline surfacing, which this flow doesn't
          // have. Surface them here instead so a publish failure is never silently dropped.
          const parsed = parseApiError(error);
          if (parsed.statusCode === 409 || parsed.statusCode === 422) {
            toast.error(t('publicLink.autoPublishFailed'));
          }
        },
      },
    );
  }

  function handleGenerate() {
    // Only auto-publish when we genuinely KNOW there's no published version yet. When the
    // publish-status check itself is unknown (e.g. the versions-list fetch failed),
    // hasPublishedVersion is also false but that's not the same thing — fall through to
    // generating directly and let the server (the source of truth) decide.
    if (!hasPublishedVersion && !isUnknownPublishStatus) {
      handleAutoPublishThenCreate();
      return;
    }
    handleCreate();
  }

  async function handleCopy() {
    if (relativePath == null) return;
    await writeToClipboard(window.location.origin + relativePath);
    toast.success(t('publicLink.copied'));
  }

  function handleRevoke() {
    revokeMutation.mutate(undefined, {
      onSuccess: () => {
        setGeneratedLink(null);
        clearCachedPublicLink(offerId, storage);
        toast.success(t('publicLink.revoked'));
      },
    });
  }

  function closeConfirmDialog() {
    setActiveConfirmDialog(null);
  }

  function handleConfirmRegenerate() {
    closeConfirmDialog();
    handleCreate();
  }

  function handleConfirmRevoke() {
    closeConfirmDialog();
    handleRevoke();
  }

  // ── Inactive: no link yet ────────────────────────────────────
  if (hasNoLinkYet(hasActiveLink, canShowCopy)) {
    return (
      <InactiveLinkView
        isCheckingPublishStatus={isCheckingPublishStatus}
        isGenerating={isAnyMutationPending(createMutation.isPending, publishVersion.isPending)}
        labels={{
          generate: t('publicLink.generateShort'),
          inactive: t('publicLink.inactive'),
        }}
        onGenerate={handleGenerate}
      />
    );
  }

  // ── Active ───────────────────────────────────────────────────
  return (
    <>
      <ActiveLinkView
        absoluteUrl={resolveAbsoluteUrl(canShowCopy, relativePath)}
        copyHint={canShowRegenerateToCopy}
        expiresAt={expiresAt}
        firstViewedAt={resolveFirstViewedAt(status)}
        isRegenerating={createMutation.isPending}
        isRevoking={revokeMutation.isPending}
        labels={{
          active: t('publicLink.active'),
          copyHint: t('publicLink.copyHint'),
          expires: (date) => t('publicLink.expires', { date }),
          notViewedYet: t('publicLink.notViewedYet'),
          regenerate: t('publicLink.regenerate'),
          regenerateToCopy: t('publicLink.regenerateToCopy'),
          revoke: t('publicLink.revoke'),
          viewedAt: (date) => t('publicLink.viewedAt', { date }),
        }}
        onCopy={() => void handleCopy()}
        onRegenerate={() => setActiveConfirmDialog('regenerate')}
        onRevoke={() => setActiveConfirmDialog('revoke')}
      />
      <PublicLinkConfirmDialog
        acknowledgementLabel={t('publicLink.regenerateDialog.acknowledgement')}
        body={t('publicLink.regenerateDialog.body')}
        cancelLabel={t('publicLink.cancel')}
        confirmLabel={t('publicLink.regenerate')}
        isOpen={activeConfirmDialog === 'regenerate'}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmRegenerate}
        title={t('publicLink.regenerateDialog.title')}
      />
      <PublicLinkConfirmDialog
        acknowledgementLabel={t('publicLink.revokeDialog.acknowledgement')}
        body={t('publicLink.revokeDialog.body')}
        cancelLabel={t('publicLink.cancel')}
        confirmLabel={t('publicLink.revoke')}
        isOpen={activeConfirmDialog === 'revoke'}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmRevoke}
        title={t('publicLink.revokeDialog.title')}
      />
    </>
  );
}
